import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, KeyRound, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PasswordChangeModal = ({ isOpen, onComplete }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Strong password validation
  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) errors.push('At least 8 characters');
    if (!/[A-Z]/.test(password)) errors.push('One uppercase letter');
    if (!/[a-z]/.test(password)) errors.push('One lowercase letter');
    if (!/[0-9]/.test(password)) errors.push('One number');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) errors.push('One special character (!@#$%^&*...)');
    return errors;
  };

  const passwordErrors = validatePassword(newPassword);
  const isPasswordStrong = passwordErrors.length === 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Enforce strong password requirements
    if (!isPasswordStrong) {
      toast.error('Please create a stronger password');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ new_password: newPassword })
      });

      if (response.ok) {
        toast.success('Password changed successfully!');
        onComplete();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to change password');
      }
    } catch (error) {
      toast.error('Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = (password) => {
    if (password.length === 0) return { strength: 0, label: '', color: '' };
    
    const errors = validatePassword(password);
    const score = 5 - errors.length; // 5 requirements total
    
    if (score <= 1) return { strength: 20, label: 'Very Weak', color: 'bg-red-500' };
    if (score === 2) return { strength: 40, label: 'Weak', color: 'bg-orange-500' };
    if (score === 3) return { strength: 60, label: 'Fair', color: 'bg-yellow-500' };
    if (score === 4) return { strength: 80, label: 'Good', color: 'bg-lime-500' };
    return { strength: 100, label: 'Strong ✓', color: 'bg-green-500' };
  };

  const passwordStrength = getPasswordStrength(newPassword);

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="bg-white border-none rounded-3xl max-w-md" hideClose>
        <DialogHeader>
          <div className="flex flex-col items-center text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200 }}
              className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4"
            >
              <KeyRound className="w-8 h-8 text-primary" />
            </motion.div>
            <DialogTitle className="font-heading text-2xl font-bold">Set Your Password</DialogTitle>
            <p className="text-muted-foreground mt-2 text-sm">
              For security, please create a new password to replace your temporary one.
            </p>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label className="text-foreground">New Password</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Create a strong password"
                className="pl-10 pr-10 h-12 rounded-xl"
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            
            {/* Password strength indicator */}
            {newPassword && (
              <div className="mt-2">
                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${passwordStrength.strength}%` }}
                    className={`h-full ${passwordStrength.color}`}
                  />
                </div>
                <p className={`text-xs mt-1 ${
                  passwordStrength.strength >= 75 ? 'text-green-600' : 
                  passwordStrength.strength >= 50 ? 'text-yellow-600' : 'text-red-600'
                }`}>
                  {passwordStrength.label}
                </p>
              </div>
            )}
          </div>

          <div>
            <Label className="text-foreground">Confirm Password</Label>
            <div className="relative mt-1">
              <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                className="pl-10 h-12 rounded-xl"
                data-testid="confirm-password-input"
              />
            </div>
            {confirmPassword && newPassword !== confirmPassword && (
              <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
            )}
            {confirmPassword && newPassword === confirmPassword && (
              <p className="text-xs text-green-600 mt-1">Passwords match!</p>
            )}
          </div>

          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Password Requirements:</h4>
            <ul className="text-xs space-y-1">
              <li className={newPassword.length >= 8 ? 'text-green-600' : 'text-blue-700'}>
                {newPassword.length >= 8 ? '✓' : '○'} At least 8 characters
              </li>
              <li className={/[A-Z]/.test(newPassword) ? 'text-green-600' : 'text-blue-700'}>
                {/[A-Z]/.test(newPassword) ? '✓' : '○'} One uppercase letter (A-Z)
              </li>
              <li className={/[a-z]/.test(newPassword) ? 'text-green-600' : 'text-blue-700'}>
                {/[a-z]/.test(newPassword) ? '✓' : '○'} One lowercase letter (a-z)
              </li>
              <li className={/[0-9]/.test(newPassword) ? 'text-green-600' : 'text-blue-700'}>
                {/[0-9]/.test(newPassword) ? '✓' : '○'} One number (0-9)
              </li>
              <li className={/[!@#$%^&*(),.?":{}|<>]/.test(newPassword) ? 'text-green-600' : 'text-blue-700'}>
                {/[!@#$%^&*(),.?":{}|<>]/.test(newPassword) ? '✓' : '○'} One special character (!@#$%...)
              </li>
            </ul>
          </div>

          <Button
            type="submit"
            disabled={loading || !isPasswordStrong || newPassword !== confirmPassword}
            className="w-full bg-primary hover:bg-primary-dark rounded-xl h-12"
            data-testid="set-password-btn"
          >
            {loading ? 'Setting Password...' : 'Set Password & Continue'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PasswordChangeModal;
